import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { setProspectCustom, updateProspect } from "../lib/api";

const CORE = [["name", "Name"], ["role", "Job title"], ["company", "Company"], ["email", "Email"], ["phone", "Phone (with country code)"], ["location", "City / location"], ["linkedin_url", "LinkedIn"], ["industry", "Industry"]];

const INPUT_TYPE = { number: "number", date: "date", email: "email", url: "url" };

// One contact: what a call or import captured, and the company's own fields to read or edit.
export default function ProspectDetails({ prospect, fields, close, onSaved }) {
  const [values, setValues] = useState(() => Object.fromEntries(fields.map(f => [f.key, prospect.custom?.[f.key] ?? ""])));
  const [core, setCore] = useState(() => Object.fromEntries(CORE.map(([k]) => [k, prospect[k] || ""])));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      let latest = null;
      const coreChanged = Object.fromEntries(CORE.filter(([k]) => core[k] !== (prospect[k] || "")).map(([k]) => [k, core[k]]));
      if (Object.keys(coreChanged).length) latest = await updateProspect(prospect.id, coreChanged);
      const changed = Object.fromEntries(fields
        .filter(f => String(values[f.key] ?? "") !== String(prospect.custom?.[f.key] ?? ""))
        .map(f => [f.key, values[f.key]]));
      if (Object.keys(changed).length) latest = await setProspectCustom(prospect.id, changed);
      if (latest) onSaved(latest);
      toast.success("Saved"); close();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save");
    } finally { setBusy(false); }
  };

  const input = (f) => {
    const v = values[f.key], set = (x) => setValues(s => ({ ...s, [f.key]: x }));
    if (f.type === "dropdown") return <select value={v} onChange={e => set(e.target.value)}><option value="">—</option>{f.options.map(o => <option key={o}>{o}</option>)}</select>;
    if (f.type === "yes_no") return <select value={v === true ? "yes" : v === false ? "no" : ""} onChange={e => set(e.target.value)}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select>;
    return <input type={INPUT_TYPE[f.type] || "text"} value={v} onChange={e => set(e.target.value)} />;
  };

  return <div className="modal-backdrop" onClick={close}>
    <div className="modal login-card setup-card" onClick={e => e.stopPropagation()} data-testid="prospect-details">
      <button className="modal-close" onClick={close}><X size={16} /></button>
      <h2>Contact</h2>
      <p>Fill in anything missing. An empty company or name can stop a sequence from sending, because the email template uses it.</p>
      <fieldset className="key-group"><legend>Contact details</legend>
        <div className="two-col">{CORE.map(([k, label]) => <label key={k}>{label}<input value={core[k]} onChange={e => setCore(c => ({ ...c, [k]: e.target.value }))} data-testid={`detail-${k}`} /></label>)}</div>
      </fieldset>
      {prospect.notes && <fieldset className="key-group"><legend>Notes{prospect.source === "call" ? " from the call" : ""}</legend><p className="notes-text">{prospect.notes}</p></fieldset>}
      {fields.length > 0 && <fieldset className="key-group"><legend>Your fields</legend>
        {fields.map(f => <label key={f.key}>{f.label}{input(f)}</label>)}
      </fieldset>}
      {fields.length === 0 && <p className="muted">Add your own fields under Settings → Your own contact fields.</p>}
      <button className="primary-button full" onClick={save} disabled={busy} data-testid="prospect-details-save">{busy ? "Saving…" : "Save"}</button>
    </div>
  </div>;
}
